import type { GameEngine } from '../game/GameEngine';
import type { UpcomingEnemyPreview } from '../game/types';
import { t } from '../i18n';

export class HudController {
  private level = required('hud-level');
  private score = required('hud-score');
  private p1 = required('hud-p1');
  private p2 = required('hud-p2');
  private p3 = required('hud-p3');
  private p4 = required('hud-p4');
  private enemies = required('hud-enemies');
  private base = required('hud-base');
  private playerSlots = [
    { element: this.p1 },
    { element: this.p2 },
    { element: this.p3 },
    { element: this.p4 },
  ];

  update(engine: GameEngine): void {
    const stats = engine.getStats();
    this.level.textContent = String(stats.level);
    this.score.textContent = String(stats.score);
    this.playerSlots.forEach((slot, index) => {
      const player = stats.players[index];
      const group = slot.element.closest('.hud-group');
      group?.classList.toggle('disabled', !player?.active);
      group?.classList.toggle('hidden', !player?.active);
      slot.element.innerHTML = player?.active
        ? playerHud(engine.mode.infiniteLives ? '∞' : String(player.lives), player.hp, player.maxHp, player.upgradeLevel)
        : `<span class="hud-disabled">${t('hud.idle')}</span>`;
    });
    this.enemies.innerHTML = enemyHud(stats.enemiesRemaining, stats.upcomingWave);
    this.base.textContent = stats.baseState;
  }
}

function playerHud(lives: string, hp: number, maxHp: number, upgradeLevel: number): string {
  const safeMax = Math.max(1, maxHp);
  const ratio = Math.max(0, Math.min(1, hp / safeMax));
  const hpLabel = `${Math.ceil(Math.max(0, hp))}/${safeMax}`;
  return `
    <span class="player-topline">
      <span class="player-life">${lives}</span>
      <span class="level-badge">Lv.${upgradeLevel}</span>
    </span>
    <span class="player-bottomline">
      <span class="hp-meter" aria-label="${t('hud.hp', { value: hpLabel })}">
        <span style="width:${ratio * 100}%"></span>
      </span>
      <span class="hp-text">${hpLabel}</span>
    </span>
  `;
}

function enemyHud(total: number, upcomingWave: UpcomingEnemyPreview[]): string {
  const chips = upcomingWave.length > 0
    ? upcomingWave.map((entry) => {
      const key = entry.kind === 'boss'
        ? entry.bossVariant
          ? `bossVariant.preview.${entry.bossVariant}`
          : 'enemy.short.boss'
        : `enemy.short.${entry.kind}`;
      return `<span class="enemy-chip ${entry.kind === 'boss' ? 'boss' : ''}">${t(key)}</span>`;
    }).join('')
    : `<span class="enemy-chip idle">${t('hud.waveClear')}</span>`;

  return `
    <span class="enemy-total">${total}</span>
    <span class="enemy-preview-row">
      <span class="enemy-preview-label">${t('hud.wave')}</span>
      <span class="enemy-preview-chips">${chips}</span>
    </span>
  `;
}

function required(id: string): HTMLElement {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Missing element #${id}`);
  return element;
}
